import { useCallback, useRef, useState } from "react";

const MEDIA_MODE = Object.freeze({
  AUDIO_VIDEO: "AUDIO_VIDEO",
  AUDIO_ONLY: "AUDIO_ONLY",
});

const requestMedia = async (constraints) => {
  try {
    const stream =
      await navigator.mediaDevices.getUserMedia(constraints);

    return {
      stream,
      errorType: null,
    };
  } catch (error) {
    return {
      stream: null,
      errorType: error?.name || "unknown",
    };
  }
};

const useDevices = (videoRef) => {
  const [camStatus, setCamStatus] =
    useState("checking");

  const [micStatus, setMicStatus] =
    useState("checking");

  const [speakerStatus, setSpeakerStatus] =
    useState("idle");

  const [
    speakerCheckActive,
    setSpeakerCheckActive,
  ] = useState(false);

  const [micLevel, setMicLevel] =
    useState(0);

  const [camDevices, setCamDevices] =
    useState([]);

  const [activeCamId, setActiveCamId] =
    useState("");

  const [deviceError, setDeviceError] =
    useState(null);

  const [camOn, setCamOn] =
    useState(false);

  const [micOn, setMicOn] =
    useState(false);

  const streamRef = useRef(null);

  const analyserRef = useRef(null);

  const animationFrameRef = useRef(null);

  const audioContextRef = useRef(null);

  const speakerTimerRef = useRef(null);

  /*
   * =========================================================
   * SPEAKER TIMER
   * =========================================================
   */

  const clearSpeakerTimer = useCallback(() => {
    if (speakerTimerRef.current) {
      window.clearTimeout(
        speakerTimerRef.current,
      );

      speakerTimerRef.current = null;
    }
  }, []);

  const scheduleSpeakerCheck =
    useCallback(() => {
      clearSpeakerTimer();

      speakerTimerRef.current =
        window.setTimeout(() => {
          setSpeakerCheckActive(true);

          speakerTimerRef.current = null;
        }, 1200);
    }, [clearSpeakerTimer]);

  /*
   * =========================================================
   * MICROPHONE ANALYSER
   * =========================================================
   */

  const stopMicAnalyser = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(
        animationFrameRef.current,
      );

      animationFrameRef.current = null;
    }

    analyserRef.current = null;

    if (audioContextRef.current) {
      audioContextRef.current
        .close()
        .catch(() => {
          // Ignore browser close errors.
        });

      audioContextRef.current = null;
    }

    setMicLevel(0);
  }, []);

  const setupMicAnalyser = useCallback(
    (stream) => {
      stopMicAnalyser();

      const AudioContext =
        window.AudioContext ||
        window.webkitAudioContext;

      /*
       * Mic analyser is only a visual helper.
       * If AudioContext is unavailable, the
       * microphone itself may still work.
       */
      if (!AudioContext) {
        setMicStatus("pass");

        return;
      }

      try {
        const audioContext =
          new AudioContext();

        const analyser =
          audioContext.createAnalyser();

        analyser.fftSize = 256;

        const source =
          audioContext.createMediaStreamSource(
            stream,
          );

        source.connect(analyser);

        audioContextRef.current =
          audioContext;

        analyserRef.current = analyser;

        const data =
          new Uint8Array(
            analyser.frequencyBinCount,
          );

        const updateMicLevel = () => {
          if (!analyserRef.current) {
            return;
          }

          analyserRef.current.getByteFrequencyData(
            data,
          );

          const average =
            data.reduce(
              (total, value) =>
                total + value,
              0,
            ) / data.length;

          setMicLevel(average);

          animationFrameRef.current =
            requestAnimationFrame(
              updateMicLevel,
            );
        };

        updateMicLevel();

        setMicStatus("pass");
      } catch (error) {
        /*
         * Do not mark the microphone as failed
         * simply because its visual analyser
         * could not start.
         */
        setMicStatus("pass");
      }
    },
    [stopMicAnalyser],
  );

  /*
   * =========================================================
   * STOP / CLEANUP
   * =========================================================
   */

  const stopDevices = useCallback(() => {
    clearSpeakerTimer();

    setSpeakerCheckActive(false);

    streamRef.current
      ?.getTracks()
      .forEach((track) => {
        track.stop();
      });

    streamRef.current = null;

    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }

    stopMicAnalyser();

    setCamOn(false);
    setMicOn(false);
  }, [
    clearSpeakerTimer,
    stopMicAnalyser,
    videoRef,
  ]);

  /*
   * =========================================================
   * START DEVICES
   * =========================================================
   *
   * AUDIO_VIDEO
   * → request camera + microphone.
   *
   * AUDIO_ONLY
   * → request microphone only.
   * → camera is never requested.
   *
   * Important:
   * AUDIO_ONLY is an explicit privacy choice,
   * not merely an error fallback.
   */

  const startDevices = useCallback(
    async ({
      mediaMode = MEDIA_MODE.AUDIO_VIDEO,
    } = {}) => {
      /*
       * Always stop the previous stream first.
       * This keeps retries and mode changes clean.
       */
      stopDevices();

      const isAudioOnly =
        mediaMode ===
        MEDIA_MODE.AUDIO_ONLY;

      setCamStatus(
        isAudioOnly
          ? "idle"
          : "checking",
      );

      setMicStatus("checking");

      setSpeakerStatus("idle");

      setSpeakerCheckActive(false);

      setDeviceError(null);

      setCamDevices([]);

      setActiveCamId("");

      setCamOn(false);

      setMicOn(false);

      /*
       * Browser capability guard.
       */
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices
          ?.getUserMedia
      ) {
        setCamStatus(
          isAudioOnly
            ? "idle"
            : "fail",
        );

        setMicStatus("fail");

        setDeviceError(
          "SecurityError",
        );

        return {
          success: false,
          mediaMode,
          errorType:
            "SecurityError",
          audioOnlyAvailable: false,
        };
      }

      /*
       * =====================================================
       * AUDIO ONLY / PRIVACY MODE
       * =====================================================
       */

      if (isAudioOnly) {
        const audioResult =
          await requestMedia({
            video: false,
            audio: true,
          });

        if (!audioResult.stream) {
          setCamStatus("idle");
          setMicStatus("fail");

          setCamOn(false);
          setMicOn(false);

          const errorType =
            audioResult.errorType ||
            "unknown";

          setDeviceError(
            errorType,
          );

          return {
            success: false,
            mediaMode:
              MEDIA_MODE.AUDIO_ONLY,
            errorType,
            audioOnlyAvailable: false,
          };
        }

        const stream =
          audioResult.stream;

        const audioTrack =
          stream.getAudioTracks()[0];

        /*
         * A stream without an actual audio track
         * should not count as success.
         */
        if (!audioTrack) {
          stream
            .getTracks()
            .forEach((track) =>
              track.stop(),
            );

          setCamStatus("idle");
          setMicStatus("fail");

          setCamOn(false);
          setMicOn(false);

          setDeviceError(
            "NotFoundError",
          );

          return {
            success: false,
            mediaMode:
              MEDIA_MODE.AUDIO_ONLY,
            errorType:
              "NotFoundError",
            audioOnlyAvailable: false,
          };
        }

        streamRef.current =
          stream;

        setCamStatus("idle");

        setCamOn(false);

        setMicStatus("pass");

        setMicOn(
          Boolean(
            audioTrack.enabled,
          ),
        );

        setupMicAnalyser(
          stream,
        );

        scheduleSpeakerCheck();

        return {
          success: true,
          mediaMode:
            MEDIA_MODE.AUDIO_ONLY,
          microphoneAvailable: true,
          cameraAvailable: false,
          audioOnlyAvailable: true,
        };
      }

      /*
       * =====================================================
       * AUDIO + VIDEO MODE
       * =====================================================
       */

      const fullMediaResult =
        await requestMedia({
          video: true,
          audio: true,
        });

      if (
        fullMediaResult.stream
      ) {
        const stream =
          fullMediaResult.stream;

        const videoTrack =
          stream.getVideoTracks()[0];

        const audioTrack =
          stream.getAudioTracks()[0];

        streamRef.current =
          stream;

        /*
         * Attach local preview.
         */
        if (
          videoRef?.current &&
          videoTrack
        ) {
          videoRef.current.srcObject =
            stream;
        }

        setCamStatus(
          videoTrack
            ? "pass"
            : "fail",
        );

        setMicStatus(
          audioTrack
            ? "pass"
            : "fail",
        );

        setCamOn(
          Boolean(
            videoTrack?.enabled,
          ),
        );

        setMicOn(
          Boolean(
            audioTrack?.enabled,
          ),
        );

        if (audioTrack) {
          setupMicAnalyser(
            stream,
          );

          scheduleSpeakerCheck();
        }

        /*
         * Camera device list becomes useful only
         * after browser permission has been granted.
         */
        if (videoTrack) {
          try {
            const allDevices =
              await navigator.mediaDevices
                .enumerateDevices();

            const cameras =
              allDevices.filter(
                (device) =>
                  device.kind ===
                  "videoinput",
              );

            setCamDevices(
              cameras,
            );
          } catch (error) {
            setCamDevices([]);
          }

          setActiveCamId(
            videoTrack
              .getSettings()
              ?.deviceId || "",
          );
        }

        /*
         * getUserMedia({video:true,audio:true})
         * normally gives both tracks.
         *
         * Still fail safely if one is unexpectedly
         * missing.
         */
        if (
          videoTrack &&
          audioTrack
        ) {
          return {
            success: true,
            mediaMode:
              MEDIA_MODE.AUDIO_VIDEO,
            microphoneAvailable: true,
            cameraAvailable: true,
            audioOnlyAvailable: true,
          };
        }

        /*
         * If camera exists but microphone doesn't,
         * the consultation cannot currently proceed.
         */
        if (
          videoTrack &&
          !audioTrack
        ) {
          setDeviceError(
            "NotFoundError",
          );

          return {
            success: false,
            mediaMode:
              MEDIA_MODE.AUDIO_VIDEO,
            errorType:
              "NotFoundError",
            microphoneAvailable: false,
            cameraAvailable: true,
            audioOnlyAvailable: false,
          };
        }

        /*
         * Extremely defensive case:
         * microphone exists but video did not.
         *
         * Keep the microphone so Waiting Room can
         * explicitly offer Privacy Mode.
         */
        if (
          !videoTrack &&
          audioTrack
        ) {
          setDeviceError(
            "NotFoundError",
          );

          return {
            success: false,
            mediaMode:
              MEDIA_MODE.AUDIO_VIDEO,
            errorType:
              "NotFoundError",
            microphoneAvailable: true,
            cameraAvailable: false,
            audioOnlyAvailable: true,
          };
        }
      }

      /*
       * =====================================================
       * AUDIO + VIDEO REQUEST FAILED
       * =====================================================
       *
       * Test microphone separately.
       *
       * Important:
       * this does NOT silently switch the user's
       * selected consultation mode.
       *
       * It only tells Waiting Room:
       *
       * "Video failed, but Audio Only is possible."
       */

      const audioOnlyResult =
        await requestMedia({
          video: false,
          audio: true,
        });

      if (
        audioOnlyResult.stream
      ) {
        const stream =
          audioOnlyResult.stream;

        const audioTrack =
          stream.getAudioTracks()[0];

        if (!audioTrack) {
          stream
            .getTracks()
            .forEach((track) =>
              track.stop(),
            );

          setCamStatus("fail");
          setMicStatus("fail");

          setCamOn(false);
          setMicOn(false);

          const errorType =
            fullMediaResult.errorType ||
            "NotFoundError";

          setDeviceError(
            errorType,
          );

          return {
            success: false,
            mediaMode:
              MEDIA_MODE.AUDIO_VIDEO,
            errorType,
            microphoneAvailable: false,
            cameraAvailable: false,
            audioOnlyAvailable: false,
          };
        }

        streamRef.current =
          stream;

        setCamStatus("fail");

        setMicStatus("pass");

        setCamOn(false);

        setMicOn(
          Boolean(
            audioTrack.enabled,
          ),
        );

        setupMicAnalyser(
          stream,
        );

        scheduleSpeakerCheck();

        const errorType =
          fullMediaResult.errorType ||
          "unknown";

        setDeviceError(
          errorType,
        );

        return {
          success: false,
          mediaMode:
            MEDIA_MODE.AUDIO_VIDEO,
          errorType,
          microphoneAvailable: true,
          cameraAvailable: false,
          audioOnlyAvailable: true,
        };
      }

      /*
       * =====================================================
       * NOTHING AVAILABLE
       * =====================================================
       */

      setCamStatus("fail");

      setMicStatus("fail");

      setCamOn(false);

      setMicOn(false);

      const errorType =
        fullMediaResult.errorType ||
        audioOnlyResult.errorType ||
        "unknown";

      setDeviceError(
        errorType,
      );

      return {
        success: false,
        mediaMode:
          MEDIA_MODE.AUDIO_VIDEO,
        errorType,
        microphoneAvailable: false,
        cameraAvailable: false,
        audioOnlyAvailable: false,
      };
    },
    [
      scheduleSpeakerCheck,
      setupMicAnalyser,
      stopDevices,
      videoRef,
    ],
  );

  /*
   * =========================================================
   * CAMERA TOGGLE
   * =========================================================
   */

  const toggleCam = useCallback(() => {
    const videoTracks =
      streamRef.current
        ?.getVideoTracks() ||
      [];

    if (!videoTracks.length) {
      return false;
    }

    const nextEnabled =
      !videoTracks[0].enabled;

    videoTracks.forEach(
      (track) => {
        track.enabled =
          nextEnabled;
      },
    );

    setCamOn(
      nextEnabled,
    );

    /*
     * If VideoPreviewCard conditionally removes
     * its <video> element while camera is off,
     * attach the existing MediaStream again after
     * React mounts it back.
     */
    if (
      nextEnabled &&
      streamRef.current
    ) {
      window.setTimeout(() => {
        if (
          videoRef?.current &&
          streamRef.current
        ) {
          videoRef.current.srcObject =
            streamRef.current;
        }
      }, 0);
    }

    return true;
  }, [videoRef]);

  /*
   * =========================================================
   * MICROPHONE TOGGLE
   * =========================================================
   */

  const toggleMic = useCallback(() => {
    const audioTracks =
      streamRef.current
        ?.getAudioTracks() ||
      [];

    if (!audioTracks.length) {
      return false;
    }

    const nextEnabled =
      !audioTracks[0].enabled;

    audioTracks.forEach(
      (track) => {
        track.enabled =
          nextEnabled;
      },
    );

    setMicOn(
      nextEnabled,
    );

    return true;
  }, []);

  /*
   * =========================================================
   * CAMERA SWITCHING
   * =========================================================
   */

  const switchCamera = useCallback(
    async (deviceId) => {
      if (
        !deviceId ||
        !streamRef.current
      ) {
        return false;
      }

      try {
        const newStream =
          await navigator.mediaDevices
            .getUserMedia({
              video: {
                deviceId: {
                  exact:
                    deviceId,
                },
              },

              audio: false,
            });

        const newVideoTrack =
          newStream
            .getVideoTracks()[0];

        if (!newVideoTrack) {
          newStream
            .getTracks()
            .forEach(
              (track) =>
                track.stop(),
            );

          return false;
        }

        const currentVideoTracks =
          streamRef.current
            .getVideoTracks();

        currentVideoTracks.forEach(
          (track) => {
            streamRef.current
              .removeTrack(track);

            track.stop();
          },
        );

        streamRef.current.addTrack(
          newVideoTrack,
        );

        if (videoRef?.current) {
          videoRef.current.srcObject =
            streamRef.current;
        }

        setActiveCamId(
          deviceId,
        );

        setCamStatus("pass");

        setCamOn(
          Boolean(
            newVideoTrack.enabled,
          ),
        );

        return true;
      } catch (error) {
        return false;
      }
    },
    [videoRef],
  );

  /*
   * =========================================================
   * PUBLIC HOOK CONTRACT
   * =========================================================
   */

  return {
    camStatus,
    micStatus,

    speakerStatus,
    setSpeakerStatus,

    speakerCheckActive,
    setSpeakerCheckActive,

    micLevel,

    camDevices,
    activeCamId,

    deviceError,

    camOn,
    micOn,

    streamRef,

    startDevices,
    stopDevices,

    toggleCam,
    toggleMic,

    switchCamera,
  };
};

export {
  MEDIA_MODE,
};

export default useDevices;